<script>

import MembershipLevel from '@/model/dataLayer/api/MembershipSystem/MembershipLevel'
import { showErrorMessage, showSuccessMessage } from '@/model/utils/utils'
import { getMembershipLevelFormSchema } from '@/model/baseForm/membershipLevelFormSchema'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'
import IKUtils from 'innerken-js-utils'

export default {
  name: 'MembershipLevel',
  components: {
    PageSubHeader
  },
  data () {
    return {
      levels: [],
      maxLevels: 5,
      loading: false,
      submitting: false,
      model: MembershipLevel
    }
  },
  async mounted () {
    await this.reload()
    await this.loadLevels()
  },
  methods: {
    async loadLevels () {
      this.levels = await MembershipLevel.getList(true)
    },
    async openAddForm () {
      if (this.levels.length >= this.maxLevels) {
        showErrorMessage(`最多只能添加${this.maxLevels}个等级`)
        return
      }

      const store = useBaseFormDialogStore()

      // Prepare the form schema
      const formSchema = getMembershipLevelFormSchema()

      // Open the dialog
      const formData = await store.editItem({
        title: '添加会员等级',
        subtitle: '创建新的会员等级',
        schemas: formSchema
      }, null)

      // Handle the result
      if (formData) {
        await this.handleFormSubmit(formData, null)
      }
    },
    async openEditForm (level) {
      const store = useBaseFormDialogStore()

      // Deep copy the level to avoid modifying the original
      const editingLevel = IKUtils.deepCopy(level)

      // Prepare the form schema
      const formSchema = getMembershipLevelFormSchema()

      // Open the dialog
      const formData = await store.editItem({
        title: '编辑会员等级',
        subtitle: '修改会员等级信息',
        schemas: formSchema
      }, editingLevel)

      // Handle the result
      if (formData) {
        await this.handleFormSubmit(formData, editingLevel)
      }
    },
    async handleFormSubmit (formData, editingLevel) {
      this.submitting = true
      try {
        if (editingLevel) {
          // Edit existing level
          formData.id = editingLevel.id
          await MembershipLevel.edit(formData)
          showSuccessMessage('会员等级更新成功')
        } else {
          // Add new level
          await MembershipLevel.add(formData)
          showSuccessMessage('会员等级添加成功')
        }

        await this.loadLevels()
        if (this.$refs.table) {
          await this.$refs.table.reload()
        }

        // Emit an event to notify that membership levels have been updated
        this.$emit('membership-levels-updated')
      } catch (error) {
        console.error('Error saving membership level:', error)
        showErrorMessage('保存失败: ' + (error.message || '未知错误'))
      } finally {
        this.submitting = false
      }
    },
    async reload () {
      this.loading = true
      try {
        await this.loadLevels()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<template>
  <v-container>
    <div
      v-if="loading"
      class="text-center my-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      />
      <div class="mt-4 text-body-1 grey--text text--darken-1">
        加载中...
      </div>
    </div>

    <div v-else>
      <page-sub-header class="mb-4">
        会员等级
        <template #subtitle>
          最多只能添加 {{ maxLevels }} 个等级，当前已添加 {{ levels.length }} 个
        </template>
        <template #action>
          <v-btn
            color="amber lighten-4 black--text"
            elevation="0"
            @click="openAddForm"
          >
            <v-icon left>
              mdi-plus
            </v-icon>
            {{ $t('add') }}
          </v-btn>
        </template>
      </page-sub-header>

      <div
        v-if="levels.length === 0"
        class="text-center pa-4"
      >
        <v-icon
          size="64"
          color="grey lighten-1"
        >
          mdi-account-group
        </v-icon>
        <div class="text-h6 grey--text text--darken-1 mt-2">
          暂无会员等级
        </div>
        <div class="text-body-2 grey--text">
          点击添加按钮创建第一个会员等级
        </div>
      </div>

      <div v-else>
        <div class="level-container">
          <div
            v-for="level in levels"
            :key="level.id"
            class="level-item"
          >
            <v-card
              class="level-card"
              :style="{borderRight: `4px solid ${level.color || '#e0e0e0'}`}"
              elevation="2"
            >
              <div
                class="level-content flex-grow-1"
              >
                <v-card-title class="d-flex align-center pt-3 pb-1">
                  <div class="text-truncate">
                    {{ level.name }}
                  </div>
                </v-card-title>

                <v-card-text class="pt-0">
                  <div
                    v-if="level.description"
                    class="text-body-2 mb-4 text-truncate"
                  >
                    {{ level.description }}
                  </div>

                  <v-chip
                    class="mr-2 mb-2"
                    color="blue-grey lighten-5"
                  >
                    {{ '所需积分' }}: {{ level.requiredPoints }}
                  </v-chip>

                  <v-chip
                    class="mr-2 mb-2"
                    color="blue-grey lighten-5"
                  >
                    {{ '等级' }}: {{ level.levelOrder || 0 }}
                  </v-chip>
                </v-card-text>
              </div>

              <div
                class="level-image-container d-flex flex-column flex-grow-0 flex-shrink-1"
              >
                <div class="level-image-actions">
                  <v-btn
                    text
                    small
                    color="primary"
                    @click="openEditForm(level)"
                  >
                    <v-icon
                      small
                      left
                    >
                      mdi-pencil
                    </v-icon>
                    {{ $t('edit') }}
                  </v-btn>
                </div>
                <div
                  style="width: min-content"
                  class="pa-3"
                >
                  <img
                    height="90"
                    v-if="level.image"
                    :src="level.image"
                  >
                  <div
                    v-else
                    class="level-image-placeholder"
                    :style="{backgroundColor: level.color || '#e0e0e0'}"
                  >
                    <span class="level-initial">{{ level.name ? level.name.charAt(0).toUpperCase() : 'L' }}</span>
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </div>
      </div>

    </div>
  </v-container>
</template>

<style scoped>
.level-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.level-item {
  margin-bottom: 16px;
  width: 100%;
}

.level-card {
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.level-card:hover {
  transform: translateX(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}

.level-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* Ensures text truncation works */
  padding: 16px 20px;
}

.v-card__title {
  word-break: break-word;
  font-size: 1.5rem !important;
  padding: 0 0 12px 0 !important;
}

.v-card__text {
  flex-grow: 1;
  padding: 0 !important;
}

.level-content .text-body-2 {
  font-size: 1.1rem !important;
}

.v-chip {
  font-size: 0.95rem !important;
  height: auto !important;
  padding: 6px 12px !important;
}

.v-card__actions {
  justify-content: flex-end;
}

.level-image-container {
  padding: 10px;
}

.level-image-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.level-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.level-card:hover .level-image {
  transform: scale(1.05);
}

.level-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.level-image-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.level-initial {
  font-size: 36px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
}
</style>
